import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Trash2, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';

const roleOptions = [
    { value: 'admin', label: 'Administrateur' },
    { value: 'office_agent', label: 'Agent Bureau' },
    { value: 'confirmateur', label: 'Agent de confirmation' },
    { value: 'vendeur', label: 'Vendeur' },
    { value: 'driver', label: 'Chauffeur/Livreur' },
];

const UserEdit = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const isNew = userId === 'nouveau';
    
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'vendeur',
        phone: '',
        telegram_chat_id: '',
        telegram_notifications_enabled: true,
        commission_model: 'none',
        commission_rate: 0,
        pa_fee_enabled: false,
        is_active: true,
    });

    useEffect(() => {
        if (!isNew) {
            fetchUser();
        }
    }, [userId]);

    const fetchUser = async () => {
        try {
            const response = await api.get('/users');
            const foundUser = response.data.find(u => u.id === userId);
            if (foundUser) {
                setFormData({
                    name: foundUser.name || '',
                    email: foundUser.email || '',
                    password: '',
                    role: foundUser.role || 'vendeur',
                    phone: foundUser.phone || '',
                    telegram_chat_id: foundUser.telegram_chat_id || '',
                    telegram_notifications_enabled: foundUser.telegram_notifications_enabled !== false,
                    commission_model: foundUser.commission_model || 'none',
                    commission_rate: foundUser.commission_rate || 0,
                    pa_fee_enabled: foundUser.pa_fee_enabled || false,
                    is_active: foundUser.is_active !== false,
                });
            } else {
                toast.error('Utilisateur non trouvé');
                navigate('/utilisateurs');
            }
        } catch (error) {
            toast.error('Erreur lors du chargement');
            navigate('/utilisateurs');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.name || !formData.email) {
            toast.error('Nom et email sont requis');
            return;
        }
        
        if (isNew && !formData.password) {
            toast.error('Mot de passe requis pour un nouvel utilisateur');
            return;
        }

        setSaving(true);
        try {
            const dataToSend = { ...formData };
            if (!dataToSend.password) {
                delete dataToSend.password;
            }

            if (isNew) {
                await api.post('/users', dataToSend);
                toast.success('Utilisateur créé avec succès');
            } else {
                await api.put(`/users/${userId}`, dataToSend);
                toast.success('Utilisateur mis à jour');
            }
            navigate('/utilisateurs');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
            return;
        }
        
        try {
            await api.delete(`/users/${userId}`);
            toast.success('Utilisateur supprimé');
            navigate('/utilisateurs');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-[50vh]">
                    <Loader2 className="w-8 h-8 animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-2xl mx-auto p-4">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/utilisateurs')}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-2xl font-bold">
                        {isNew ? 'Nouvel Utilisateur' : 'Modifier Utilisateur'}
                    </h1>
                </div>

                {/* Form */}
                <div className="space-y-4">
                    {/* Basic Info Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg">Informations de base</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <Label>Nom complet *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="Nom de l'utilisateur"
                                />
                            </div>
                            
                            <div>
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    placeholder="email@exemple.com"
                                />
                            </div>
                            
                            <div>
                                <Label>{isNew ? 'Mot de passe *' : 'Nouveau mot de passe'}</Label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        placeholder={isNew ? 'Mot de passe' : 'Laisser vide pour ne pas changer'}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </Button>
                                </div>
                            </div>
                            
                            <div>
                                <Label>Rôle</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(value) => setFormData({...formData, role: value})}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {roleOptions.map((role) => (
                                            <SelectItem key={role.value} value={role.value}>
                                                {role.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div>
                                <Label>Téléphone</Label>
                                <Input
                                    value={formData.phone}
                                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                    placeholder="+213 xxx xxx xxx"
                                />
                            </div>
                            
                            <div className="flex items-center justify-between py-2">
                                <Label>Compte actif</Label>
                                <Switch
                                    checked={formData.is_active}
                                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Telegram Card */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg">📱 Telegram</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <Label>Chat ID Telegram</Label>
                                <Input
                                    value={formData.telegram_chat_id}
                                    onChange={(e) => setFormData({...formData, telegram_chat_id: e.target.value})}
                                    placeholder="Ex: 123456789"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Envoyer /start à @NouvelleCommandeBot pour obtenir l'ID
                                </p>
                            </div>
                            
                            <div className="flex items-center justify-between py-2">
                                <Label>Notifications Telegram</Label>
                                <Switch
                                    checked={formData.telegram_notifications_enabled}
                                    onCheckedChange={(checked) => setFormData({...formData, telegram_notifications_enabled: checked})}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Commission Card - Only for vendeur */}
                    {(formData.role === 'vendeur') && (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg">💰 Commission</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Modèle de commission</Label>
                                    <Select
                                        value={formData.commission_model}
                                        onValueChange={(value) => setFormData({...formData, commission_model: value})}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Aucune</SelectItem>
                                            <SelectItem value="per_order">Par commande (DA)</SelectItem>
                                            <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                {formData.commission_model !== 'none' && (
                                    <div>
                                        <Label>
                                            {formData.commission_model === 'percentage' ? 'Taux (%)' : 'Montant (DA)'}
                                        </Label>
                                        <Input
                                            type="number"
                                            value={formData.commission_rate}
                                            onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value) || 0})}
                                            placeholder="0"
                                        />
                                    </div>
                                )}
                                
                                <div className="flex items-center justify-between py-2">
                                    <div>
                                        <Label>Frais PA 5%</Label>
                                        <p className="text-xs text-muted-foreground">Prélever 5% sur chaque commande</p>
                                    </div>
                                    <Switch
                                        checked={formData.pa_fee_enabled}
                                        onCheckedChange={(checked) => setFormData({...formData, pa_fee_enabled: checked})}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                        <Button 
                            onClick={handleSave} 
                            disabled={saving}
                            className="flex-1"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            {isNew ? 'Créer' : 'Sauvegarder'}
                        </Button>
                        
                        {!isNew && (
                            <Button 
                                variant="destructive" 
                                onClick={handleDelete}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default UserEdit;
